import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import { useStore } from '@/store';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import { useTemplatesApi } from '@/composables/templates/useTemplatesApi';
import { useToast } from 'primevue/usetoast';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';


export default _defineComponent({
  expose: [],
  setup(__props) {

const store = useStore();
const { t } = useI18n();
const toast = useToast();
const { migrateTemplatePreview, migrateTemplateConfirm } = useTemplatesApi();

const loading = ref(false);
const confirming = ref(false);
const showInfo = ref(false);
const error = ref<string | null>(null);
const originalSrcDoc = ref('');
const migratedSrcDoc = ref('');
const warnings = ref<string[]>([]);

const displayModal = computed({
  get: () => store.state.general.migrateV4ModalVisible,
  set: (val: boolean) => {
    if (!val) store.commit('general/hideMigrateV4Modal');
  },
});

const templateId = computed(() => TemplateEditorState.template?.id || undefined);

const nestjsApiUrl = computed(() => process.env.VUE_APP_NESTJS_TRACKING_API_URL || '');

const formattedWarnings = computed(() => {
  const formatted: string[] = [];
  for (const w of warnings.value) {
    if (w.startsWith('migrated_buttons:')) {
      formatted.push(t('templateBuilder.migrateV4Modal.warningButtons', { count: w.split(':')[1] }));
    } else if (w.startsWith('migrated_text_widgets:')) {
      formatted.push(t('templateBuilder.migrateV4Modal.warningText', { count: w.split(':')[1] }));
    } else if (w === 'migrated_social_icons' || w === 'migrated_social_css' || w === 'migrated_social_data') {
      if (!formatted.some((f) => f.includes('social') || f.includes('sociaux'))) {
        formatted.push(t('templateBuilder.migrateV4Modal.warningSocial'));
      }
    } else if (w === 'migrated_section_background') {
      formatted.push(t('templateBuilder.migrateV4Modal.warningSection'));
    } else if (w === 'migrated_button_css_selectors') {
      formatted.push(t('templateBuilder.migrateV4Modal.warningButtonCss'));
    } else if (w === 'migrated_product_list_margins') {
      formatted.push(t('templateBuilder.migrateV4Modal.warningProductList'));
    }
  }
  // Always add sync warning
  formatted.push(t('templateBuilder.migrateV4Modal.warningSync'));
  return [...new Set(formatted)];
});

function buildSrcDoc(bodyContent: string, cssUrl: string): string {
  // If content already contains a full HTML document, inject CSS link into it
  if (bodyContent.includes('<html') || bodyContent.includes('<head')) {
    // Inject CSS link before </head> or at the beginning
    const cssLink = `<link rel="stylesheet" href="${cssUrl}">`;
    if (bodyContent.includes('</head>')) {
      return bodyContent.replace('</head>', `${cssLink}</head>`);
    }
    if (bodyContent.includes('<head>')) {
      return bodyContent.replace('<head>', `<head>${cssLink}`);
    }
    // Fallback: prepend
    return `${cssLink}${bodyContent}`;
  }

  return `<!DOCTYPE html>
<html><head>
<meta charset="utf-8">
<link rel="stylesheet" href="${cssUrl}">
</head><body>${bodyContent}</body></html>`;
}

async function loadPreview() {
  if (!templateId.value) return;

  loading.value = true;
  error.value = null;

  const result = await migrateTemplatePreview(templateId.value);

  if (result.success && result.data) {
    warnings.value = result.data.warnings;

    const v3CssUrl = '/css/SpmAPI.css';
    const v4CssUrl = `${nestjsApiUrl.value}/api/assets/css/v4/all.css`;

    originalSrcDoc.value = buildSrcDoc(result.data.originalHtml, v3CssUrl);
    migratedSrcDoc.value = buildSrcDoc(result.data.migratedHtml, v4CssUrl);
  } else {
    error.value = result.message || t('templateBuilder.migrateV4Modal.error');
  }

  loading.value = false;
}

async function confirmMigration() {
  if (!templateId.value) return;

  confirming.value = true;

  const result = await migrateTemplateConfirm(templateId.value);

  if (result.success && result.data) {
    toast.add({
      severity: 'success',
      summary: t('templateBuilder.migrateV4Modal.successTitle'),
      detail: t('templateBuilder.migrateV4Modal.success', {
        id: result.data.id_template,
      }),
      life: 5000,
    });
    displayModal.value = false;
  } else {
    toast.add({
      severity: 'error',
      summary: t('templateBuilder.migrateV4Modal.error'),
      detail: result.message,
      life: 5000,
    });
  }

  confirming.value = false;
}

watch(displayModal, (visible) => {
  if (visible) {
    loadPreview();
  }
}, { immediate: true });

return { store, t, toast, migrateTemplatePreview, migrateTemplateConfirm, loading, confirming, showInfo, error, originalSrcDoc, migratedSrcDoc, warnings, displayModal, templateId, nestjsApiUrl, formattedWarnings, buildSrcDoc, loadPreview, confirmMigration, computed, ref, watch, useStore, useI18n, Dialog, Button, ProgressSpinner, useTemplatesApi, useToast, TemplateEditorState }
}

})